import { ServiceStoreConfig } from "../types/ServiceStoreTypes";

/**
 * Manufacturing Module Service Stores
 * Services for materials management, quality control, and NCR handling
 * Focused on ReadyMix batch plant operations
 */
export const manufacturingStores: ServiceStoreConfig[] = [
  {
    name: "cost-code",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      idField: "id",
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.costCodeStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalCodes = entities.length;
          const activeCodes = entities.filter(
            (code: Record<string, unknown>) =>
              code.isActive !== false
          ).length;
          const laborCodes = entities.filter(
            (code: Record<string, unknown>) =>
              code.type === "labor"
          ).length;
          const materialCodes = entities.filter(
            (code: Record<string, unknown>) =>
              code.type === "material"
          ).length;

          return [
            {
              title: "Total Codes",
              value: () => totalCodes.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Active",
              value: () => activeCodes.toString(),
              valueColor: "success.main",
            },
            {
              title: "Labor",
              value: () => laborCodes.toString(),
              valueColor: "info.main",
            },
            {
              title: "Material",
              value: () => materialCodes.toString(),
              valueColor: "warning.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "hold",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      idField: "id",
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.holdStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalHolds = entities.length;
          const activeHolds = entities.filter(
            (hold: Record<string, unknown>) =>
              hold.status === "active" || !hold.status
          ).length;
          const resolvedHolds = entities.filter(
            (hold: Record<string, unknown>) => hold.status === "resolved"
          ).length;
          const criticalHolds = entities.filter(
            (hold: Record<string, unknown>) =>
              hold.priority === "critical" || hold.severity === "high"
          ).length;

          return [
            {
              title: "Total Holds",
              value: () => totalHolds.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Active",
              value: () => activeHolds.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Resolved",
              value: () => resolvedHolds.toString(),
              valueColor: "success.main",
            },
            {
              title: "Critical",
              value: () => criticalHolds.toString(),
              valueColor: "error.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "materials",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      idField: "id",
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.materialsStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalMaterials = entities.length;
          const activeMaterials = entities.filter(
            (material: Record<string, unknown>) =>
              material.isActive !== false
          ).length;
          const cementMaterials = entities.filter(
            (material: Record<string, unknown>) =>
              material.category === "cement" || material.type === "cement"
          ).length;
          const aggregateMaterials = entities.filter(
            (material: Record<string, unknown>) =>
              material.category === "aggregate" || material.type === "aggregate"
          ).length;

          return [
            {
              title: "Total Materials",
              value: () => totalMaterials.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Active",
              value: () => activeMaterials.toString(),
              valueColor: "success.main",
            },
            {
              title: "Cement",
              value: () => cementMaterials.toString(),
              valueColor: "info.main",
            },
            {
              title: "Aggregate",
              value: () => aggregateMaterials.toString(),
              valueColor: "warning.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
        defaultSort: {
          sortBy: ["name"],
          sortDirection: { name: "ASC" },
        },
      },
    },
  },
  {
    name: "material-inventory",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      idField: "id",
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.materialInventoryStore as {
            entities?: Record<string, unknown>[];
          };
          const entities = store?.entities || [];
          const totalItems = entities.length;
          const lowStock = entities.filter(
            (item) => {
              const current = item.currentQuantity as number;
              const min = item.minimumQuantity as number;
              return current < min;
            }
          ).length;
          const outOfStock = entities.filter(
            (item) =>
              (item.currentQuantity as number) === 0
          ).length;
          const totalValue = entities.reduce(
            (sum, item) => {
              const qty = item.currentQuantity as number;
              const price = item.unitPrice as number;
              return sum + (qty * price || 0);
            },
            0
          );

          return [
            {
              title: "Total Items",
              value: () => totalItems.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Low Stock",
              value: () => lowStock.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Out of Stock",
              value: () => outOfStock.toString(),
              valueColor: "error.main",
            },
            {
              title: "Total Value",
              value: () => `$${(totalValue / 1000).toFixed(1)}K`,
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "material-locations",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      idField: "id",
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.materialLocationsStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalLocations = entities.length;
          const activeLocations = entities.filter(
            (loc: Record<string, unknown>) =>
              loc.isActive !== false
          ).length;
          const siloLocations = entities.filter(
            (loc: Record<string, unknown>) =>
              loc.locationType === "silo"
          ).length;
          const binLocations = entities.filter(
            (loc: Record<string, unknown>) =>
              loc.locationType === "bin"
          ).length;

          return [
            {
              title: "Total Locations",
              value: () => totalLocations.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Active",
              value: () => activeLocations.toString(),
              valueColor: "success.main",
            },
            {
              title: "Silos",
              value: () => siloLocations.toString(),
              valueColor: "info.main",
            },
            {
              title: "Bins",
              value: () => binLocations.toString(),
              valueColor: "warning.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "material-quality-certs",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      idField: "id",
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.materialQualityCertsStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalCerts = entities.length;
          const validCerts = entities.filter(
            (cert: Record<string, unknown>) => {
              const expiry = new Date(cert.expiryDate as string);
              return expiry > new Date();
            }
          ).length;
          const expiringCerts = entities.filter(
            (cert: Record<string, unknown>) => {
              const expiry = new Date(cert.expiryDate as string);
              const thirtyDaysFromNow = new Date();
              thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
              return expiry <= thirtyDaysFromNow && expiry > new Date();
            }
          ).length;
          const expiredCerts = entities.filter(
            (cert: Record<string, unknown>) => {
              const expiry = new Date(cert.expiryDate as string);
              return expiry <= new Date();
            }
          ).length;

          return [
            {
              title: "Total Certs",
              value: () => totalCerts.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Valid",
              value: () => validCerts.toString(),
              valueColor: "success.main",
            },
            {
              title: "Expiring (30d)",
              value: () => expiringCerts.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Expired",
              value: () => expiredCerts.toString(),
              valueColor: "error.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "material-reorder-rules",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      idField: "id",
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.materialReorderRulesStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalRules = entities.length;
          const activeRules = entities.filter(
            (rule: Record<string, unknown>) =>
              rule.isActive !== false
          ).length;
          const triggeredRules = entities.filter(
            (rule: Record<string, unknown>) =>
              rule.status === "triggered" || rule.lastTriggered
          ).length;

          return [
            {
              title: "Total Rules",
              value: () => totalRules.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Active",
              value: () => activeRules.toString(),
              valueColor: "success.main",
            },
            {
              title: "Triggered",
              value: () => triggeredRules.toString(),
              valueColor: "warning.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "material-transactions",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      idField: "id",
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.materialTransactionsStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalTransactions = entities.length;
          const todayTransactions = entities.filter(
            (txn: Record<string, unknown>) => {
              const txnDate = new Date(txn.createdAt as string);
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              return txnDate >= today;
            }
          ).length;
          const receipts = entities.filter(
            (txn: Record<string, unknown>) =>
              txn.transactionType === "receipt" || txn.transactionType === "in"
          ).length;
          const issues = entities.filter(
            (txn: Record<string, unknown>) =>
              txn.transactionType === "issue" || txn.transactionType === "out"
          ).length;

          return [
            {
              title: "Total Transactions",
              value: () => totalTransactions.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Today",
              value: () => todayTransactions.toString(),
              valueColor: "info.main",
            },
            {
              title: "Receipts",
              value: () => receipts.toString(),
              valueColor: "success.main",
            },
            {
              title: "Issues",
              value: () => issues.toString(),
              valueColor: "warning.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
        defaultSort: {
          sortBy: ["createdAt"],
          sortDirection: { createdAt: "DESC" },
        },
      },
    },
  },
  {
    name: "material-vendors",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      idField: "id",
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.materialVendorsStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalVendors = entities.length;
          const activeVendors = entities.filter(
            (vendor: Record<string, unknown>) =>
              vendor.status === "active" || vendor.isActive !== false
          ).length;
          const preferredVendors = entities.filter(
            (vendor: Record<string, unknown>) =>
              vendor.isPreferred === true
          ).length;
          const certifiedVendors = entities.filter(
            (vendor: Record<string, unknown>) =>
              vendor.hasCertification === true || vendor.certificationStatus === "valid"
          ).length;

          return [
            {
              title: "Total Vendors",
              value: () => totalVendors.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Active",
              value: () => activeVendors.toString(),
              valueColor: "success.main",
            },
            {
              title: "Preferred",
              value: () => preferredVendors.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Certified",
              value: () => certifiedVendors.toString(),
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "ncr",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      idField: "id",
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.ncrStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalNCRs = entities.length;
          const openNCRs = entities.filter(
            (ncr: Record<string, unknown>) =>
              ncr.status === "open" || ncr.status === "pending"
          ).length;
          const closedNCRs = entities.filter(
            (ncr: Record<string, unknown>) =>
              ncr.status === "closed" || ncr.status === "resolved"
          ).length;
          const criticalNCRs = entities.filter(
            (ncr: Record<string, unknown>) =>
              ncr.severity === "critical" || ncr.priority === "high"
          ).length;

          return [
            {
              title: "Total NCRs",
              value: () => totalNCRs.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Open",
              value: () => openNCRs.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Closed",
              value: () => closedNCRs.toString(),
              valueColor: "success.main",
            },
            {
              title: "Critical",
              value: () => criticalNCRs.toString(),
              valueColor: "error.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
        defaultSort: {
          sortBy: ["createdAt"],
          sortDirection: { createdAt: "DESC" },
        },
      },
    },
  },
  {
    name: "ncr-attachments",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      idField: "id",
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.ncrAttachmentsStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalAttachments = entities.length;
          const imageAttachments = entities.filter(
            (att: Record<string, unknown>) =>
              (att.type as string)?.includes("image") || (att.mimeType as string)?.includes("image")
          ).length;
          const documentAttachments = entities.filter(
            (att: Record<string, unknown>) =>
              (att.type as string)?.includes("pdf") || (att.mimeType as string)?.includes("pdf")
          ).length;

          return [
            {
              title: "Total Attachments",
              value: () => totalAttachments.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Images",
              value: () => imageAttachments.toString(),
              valueColor: "info.main",
            },
            {
              title: "Documents",
              value: () => documentAttachments.toString(),
              valueColor: "warning.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
];